/* Shared hardware stage: separation, a two-gesture pause, mechanical chapter, setups. */
(() => {
  const instances=new Map(),ratios=[1023/1599,572/1599,2048/749,1311/1602],stops=[0,.24,.54,1];
  const clamp=v=>Math.max(0,Math.min(1,v)),mix=(a,b,p)=>a+(b-a)*p;
  const smooth=(a,b,p)=>{const t=clamp((p-a)/(b-a));return t*t*(3-2*t)};
  const interpolate=(a,b,t)=>a.map((box,i)=>Object.fromEntries(Object.keys(box).map(k=>[k,mix(box[k],b[i][k],t)])));
  function row(width,h,gap,y){let x=(width-ratios.reduce((sum,r)=>sum+r*h,0)-gap*3)/2;return ratios.map(r=>{const box={x,y,w:r*h,h,a:1};x+=box.w+gap;return box})}
  // Align body edges instead of transparent image canvases. When connected,
  // Zero's side connector sits behind its neighbour; preserve the original photos.
  const bodyEdges=[[9/1599,1006/1599],[6/1599,559/1599],[1/749,2043/749],[9/1602,1302/1602]];
  function connected(width,h,y,indices=[0,1,2,3],custom=[]){
    const edges=indices.map((i,k)=>custom[i]?[0,ratios[i]]:i===0&&k<indices.length-1?[9/1599,978/1599]:bodyEdges[i]);
    const seam=1/749*h,total=edges.reduce((sum,[l,r])=>sum+(r-l)*h,0)-seam*(indices.length-1);
    let x=(width-total)/2;const boxes=new Map();
    indices.forEach((i,k)=>{const [l,r]=edges[k];boxes.set(i,{x:x-l*h,y,w:ratios[i]*h,h,a:1});x+=(r-l)*h-seam});
    return boxes;
  }
  function separated(mobile){return mobile?ratios.map((r,i)=>{const h=Math.min(106,153/r),w=r*h;return{x:(i%2?204:24)+(162-w)/2,y:i<2?10:194,w,h,a:1}}):row(1440,216,64,52)}
  function mount(root){
    if(instances.has(root))return;
    const events=new AbortController(),opts={signal:events.signal};
    const listen=(target,type,fn,extra={})=>target?.addEventListener(type,fn,{...opts,...extra});
    const mobile=matchMedia('(max-width:767px)'),reduced=matchMedia('(prefers-reduced-motion:reduce)');
    const cards=[...root.querySelectorAll('[data-journey-module]')],copies=[...root.querySelectorAll('[data-journey-copy]')];
    const steps=[...root.querySelectorAll('[data-journey-step]')],packages=[...root.querySelectorAll('[data-journey-package]')],packagePanel=root.querySelector('.mxj-packages');
    const custom=cards.map(card=>card.dataset.customImage==='true');
    const setups=['all','zero','core','full'],status=root.querySelector('.mxj-status'),keys=[];
    let top=44,range=1,raf=0,keyTimer=0,phase=-1,local=0,value=0,selected=0,visible=false,alive=true;
    const art=root.querySelector('.mxj-art'),backlights=[...root.querySelectorAll('.mxj-backlight')];
    let setupFade=null,wheelAt=0,wheelUntil=0,wheelTotal=0;
    let craftFrame=0,craftBlend=0,holdRemaining=2,touch=null,rangeWidth=0,rangeHeight=0;
    const separatedAt=stops[1]*.84,holdAt=stops[1]*.92,fadeDuration=440;
    const wheelThreshold=90,touchThreshold=72,gestureGap=240,gestureGuard=600;
    const staticView=()=>reduced.matches||document.body.classList.contains('mxw-editor');
    const layer=root.querySelector('.mxj-keys'),photo=cards[2]?.querySelector('img');
    if(layer&&photo){
      // Keep the original Type Core keycap crops and press timing.
      [[288,193,117,118],[410,193,117,118],[535,193,116,118],[316,316,117,117],[439,316,116,117],[562,316,117,117],[686,316,117,117],[962,439,118,118]].forEach(([x,y,w,h])=>{
        const key=document.createElement('span'),img=document.createElement('img');key.className='mxj-key';img.loading='lazy';img.decoding='async';img.src=cards[2].querySelector('.mxj-backlight')?.src||photo.src;img.alt='';
        Object.assign(key.style,{left:100*x/2048+'%',top:100*y/749+'%',width:100*w/2048+'%',height:100*h/749+'%'});
        Object.assign(img.style,{width:100*2048/w+'%',height:100*749/h+'%',left:-100*x/w+'%',top:-100*y/h+'%'});key.append(img);layer.append(key);keys.push(key);
      });
    }
    function keypress(){
      const run=phase===1&&craftBlend>.98&&!craftFrame&&visible&&!document.hidden&&!reduced.matches&&keys.length;
      if(!run){clearTimeout(keyTimer);keyTimer=0;keys.forEach(k=>k.classList.remove('is-pressed'));return}
      if(keyTimer)return;
      keys.forEach(k=>k.classList.remove('is-pressed'));keys[Math.floor(Math.random()*keys.length)].classList.add('is-pressed');
      keyTimer=setTimeout(()=>{keyTimer=0;if(alive)keypress()},340);
    }
    function backlight(){
      // Breathe only after the complete setup has faded in; use the supplied lit photos.
      const ready=alive&&phase===2&&selected!==1&&!document.hidden&&!staticView()&&setupFade?.playState!=='running'&&backlights.length&&backlights.every(img=>img.complete&&img.naturalWidth);
      // Read the moving artwork itself: an observer can retain stale bounds inside a sticky stage.
      const bounds=ready?art.getBoundingClientRect():null;
      root.classList.toggle('is-breathing',Boolean(bounds&&bounds.bottom>top&&bounds.top<innerHeight));
    }
    backlights.forEach(img=>listen(img,'load',backlight));
    function configuration(key){
      const m=mobile.matches,width=m?390:1440,sep=separated(m);
      if(key==='all')return sep;
      const included=key==='zero'?[0]:key==='core'?[0,2]:[0,1,2,3];
      const h=m?(key==='zero'?216:key==='core'?98:72):(key==='zero'?340:key==='core'?310:280);
      const y=((m?340:500)-h)/2,joined=connected(width,h,y,included,custom);
      return sep.map((b,i)=>joined.get(i)||{...b,a:0});
    }
    function paint(boxes,inspect){
      root.dataset.inspection=String(inspect&&!reduced.matches);
      cards.forEach((card,i)=>{
        const box=boxes[i],s=box.h/216,m=mobile.matches,label=card.querySelector('.mxj-label');
        Object.assign(card.style,{width:ratios[i]*216+'px',height:'216px',transform:`translate3d(${box.x}px,${box.y}px,0) scale(${s})`,opacity:String(box.a)});
        Object.assign(label.style,{top:(216+16/s)+'px',width:(m?166:Math.min(260,box.w+54))/s+'px',opacity:phase===0?String(clamp(local/.84)):String(box.a)});
        Object.assign(label.querySelector('h3').style,{fontSize:(m?(phase===2?14:18):20)/s+'px',lineHeight:(m?(phase===2?20:24):26)/s+'px'});
        Object.assign(label.querySelector('p').style,{fontSize:(m?12:14)/s+'px',lineHeight:(m?18:20)/s+'px',marginTop:8/s+'px'});
        card.tabIndex=inspect?0:-1;card.setAttribute('aria-hidden',String(box.a<.01));
        if(!inspect)card.classList.remove('is-inspecting');
      });
    }
    function animateCraft(target,duration,done){
      cancelAnimationFrame(craftFrame);craftFrame=0;
      if(staticView()){craftBlend=target;done?.();return}
      const from=craftBlend,start=performance.now();
      const tick=now=>{
        if(!alive)return;
        const t=clamp((now-start)/duration);craftBlend=mix(from,target,smooth(0,1,t));
        craftFrame=t<1?requestAnimationFrame(tick):0;draw(value);
        if(t===1)done?.();
      };
      craftFrame=requestAnimationFrame(tick);
    }
    function draw(progress){
      const previousPhase=phase;
      value=clamp(progress);const next=value<stops[1]?0:value<stops[2]?1:2;
      local=clamp((value-stops[next])/(stops[next+1]-stops[next]));
      if(phase!==next){
        phase=next;root.dataset.journeyPhase=String(phase);
        setupFade?.cancel();setupFade=null;
        cancelAnimationFrame(craftFrame);craftFrame=0;craftBlend=0;holdRemaining=2;
        if(phase===1)animateCraft(1,600);
        copies.forEach((n,i)=>{n.classList.toggle('is-current',i===phase);n.setAttribute('aria-hidden',String(i!==phase));n.inert=i!==phase});
        steps.forEach((n,i)=>n.setAttribute('aria-pressed',String(i===phase)));
        packagePanel.inert=phase!==2;packagePanel.setAttribute('aria-hidden',String(phase!==2));
        status.textContent=copies[phase].querySelector('h2').textContent;
      }
      const m=mobile.matches,width=m?390:1440,sep=separated(m);let boxes=sep;
      if(phase===0){
        const t=clamp(local/.84),assembled=[...connected(width,m?354/ratios.reduce((a,r)=>a+r,0):264,m?135:108,[0,1,2,3],custom).values()];
        boxes=interpolate(assembled,sep,t);
        copies[0].querySelectorAll('[data-assembled]').forEach(n=>{n.style.opacity=String(1-smooth(.15,.45,t));n.setAttribute('aria-hidden',String(t>=.5))});
        copies[0].querySelectorAll('[data-separated]').forEach(n=>{n.style.opacity=String(smooth(.55,.85,t));n.setAttribute('aria-hidden',String(t<.5))});
      }else if(phase===1){
        if(staticView()){cancelAnimationFrame(craftFrame);craftFrame=0;craftBlend=1}
        const t=craftBlend,h=m?96:300,y=m?132:36,pair=connected(width,h,y,[2,3],custom);
        const joined=sep.map((b,i)=>pair.get(i)||{...b,a:0});
        boxes=interpolate(sep,joined,t);
      }else{
        const active=Math.min(3,Math.floor(local*4));
        boxes=configuration(setups[active]);
        if(selected!==active||root.dataset.package===undefined||previousPhase!==2){
          selected=active;root.dataset.package=setups[active];packages.forEach((n,i)=>n.setAttribute('aria-pressed',String(i===active)));
          status.textContent=packages[active].querySelector('strong').textContent;
          // Swap complete poses, then fade in. Never expose an intermediate assembly.
          setupFade?.cancel();
          if(!staticView()){
            setupFade=art.animate([{opacity:0,transform:'translateY(6px)'},{opacity:1,transform:'translateY(0)'}],{duration:fadeDuration,easing:'ease-out'});
            setupFade.onfinish=backlight;
            wheelUntil=performance.now()+gestureGuard;
          }
        }
      }
      paint(boxes,phase===0&&local>=.84);root.dataset.progress=value.toFixed(5);root.dataset.localProgress=local.toFixed(5);keypress();
      root.dataset.holdRemaining=String(holdRemaining);root.dataset.craftProgress=craftBlend.toFixed(4);backlight();
    }
    function read(){if(staticView()||raf)return;raf=requestAnimationFrame(()=>{
      raf=0;if(!alive)return;
      const next=(top-root.getBoundingClientRect().top)/range;
      // Catch the arrival swipe without counting its remaining momentum as a new gesture.
      if(touch&&phase===0&&value<separatedAt&&next>=separatedAt){touch.used=true;touch.captured=true;seek(holdAt);return}
      if(phase===0&&next<separatedAt)holdRemaining=2;
      draw(next);
    })}
    function resize(){
      top=parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--navigation-height'))||44;
      const available=Math.max(200,(window.visualViewport?.height||innerHeight)-top),m=mobile.matches,width=document.documentElement.clientWidth;
      // Keep mobile progress stable as browser bars resize the viewport during a swipe.
      if(!m||width!==rangeWidth||!rangeHeight){rangeWidth=width;rangeHeight=available}
      range=Math.max(1700,rangeHeight*3.2)*1.5;
      root.style.setProperty('--journey-top',top+'px');root.style.setProperty('--journey-view',available+'px');
      root.style.setProperty('--journey-scale',Math.min(document.documentElement.clientWidth/(m?390:1440),available/(m?866:916)));
      root.style.setProperty('--journey-height',(available+(staticView()?0:range))+'px');
      root.dataset.scrollRange=String(range);root.classList.toggle('is-static',staticView());
      if(staticView())draw(phase<0?.22:value);else{draw((top-root.getBoundingClientRect().top)/range);read()}
    }
    function seek(progress){
      const p=clamp(progress);
      if(!staticView())window.scrollTo({top:scrollY+root.getBoundingClientRect().top-top+p*range,behavior:'instant'});
      draw(p);
    }
    function select(index,position){
      const i=Math.max(0,Math.min(2,Number(index)||0));
      const local=position===undefined?(staticView()?(i===0?.92:i===1?.5:0):.001):clamp(position);
      // Stay inside the phase even when the browser rounds the page offset to a pixel.
      seek(stops[i]+(stops[i+1]-stops[i])*Math.max(.001,Math.min(.999,local)));
    }
    const selectSetup=i=>select(2,(i+.5)/4);
    packages.forEach((n,i)=>listen(n,'click',()=>selectSetup(i)));
    function pinned(){const r=root.getBoundingClientRect();return !staticView()&&r.top<=top+2&&r.bottom>=innerHeight-2}
    function advance(direction){
      if(phase===0){
        if(direction<0)return false;
        if(value<separatedAt){seek(holdAt);return true}
        holdRemaining--;root.dataset.holdRemaining=String(holdRemaining);
        if(holdRemaining<=0)select(1,.5);
        return true;
      }
      if(phase===1){
        if(craftFrame)return true;
        // Finish the assembly's return as one timed segment, independent of wheel delta.
        animateCraft(0,400,()=>direction>0?selectSetup(0):select(0,.92));return true;
      }
      if(selected===3&&direction>0)return false;
      if(selected===0&&direction<0){select(1,.5);return true}
      selectSetup(selected+direction);return true;
    }
    // A continuous trackpad stream counts once. A fresh gesture advances the next chapter.
    listen(root,'wheel',e=>{
      if(!pinned()||e.ctrlKey||Math.abs(e.deltaX)>Math.abs(e.deltaY)||!e.deltaY)return;
      const now=performance.now(),delta=e.deltaY*(e.deltaMode===1?16:e.deltaMode===2?innerHeight:1),direction=Math.sign(delta);
      if(phase===0&&value<separatedAt){
        holdRemaining=2;
        if(direction<0||value+delta/range<separatedAt)return;
        e.preventDefault();wheelAt=now;wheelUntil=now+gestureGuard;wheelTotal=0;seek(holdAt);return;
      }
      const continued=now-wheelAt<gestureGap;wheelAt=now;
      if(craftFrame||now<wheelUntil||(continued&&wheelUntil)){e.preventDefault();return}
      wheelUntil=0;
      if((phase===0&&direction<0)||(phase===2&&selected===3&&direction>0)){wheelTotal=0;return}
      e.preventDefault();
      wheelTotal=continued&&Math.sign(wheelTotal)===direction?wheelTotal+delta:delta;
      if(Math.abs(wheelTotal)<wheelThreshold)return;
      wheelTotal=0;wheelUntil=now+gestureGuard;advance(direction);
    },{passive:false});
    listen(root,'touchstart',e=>{touch=e.touches.length===1?{x:e.touches[0].clientX,y:e.touches[0].clientY,lastY:e.touches[0].clientY,used:false,captured:false}:null},{passive:true});
    listen(root,'touchmove',e=>{
      if(!touch||e.touches.length!==1||!pinned())return;
      const dx=touch.x-e.touches[0].clientX,dy=touch.y-e.touches[0].clientY;
      if(Math.abs(dx)>Math.abs(dy))return;
      if(touch.captured){if(e.cancelable)e.preventDefault();return}
      if((phase===0&&dy<0)||(phase===2&&selected===3&&dy>0))return;
      // Cancel native scrolling before its pan threshold commits the gesture.
      if(e.cancelable)e.preventDefault();
      if(phase===0&&value<separatedAt){
        const next=value+(touch.lastY-e.touches[0].clientY)/range;touch.lastY=e.touches[0].clientY;
        if(next>=separatedAt){touch.used=true;touch.captured=true;holdRemaining=2;seek(holdAt)}else seek(next);
        return;
      }
      if(touch.used||Math.abs(dy)<touchThreshold)return;
      touch.used=true;touch.captured=true;
      if(!craftFrame&&setupFade?.playState!=='running')advance(Math.sign(dy));
    },{passive:false});
    listen(root,'touchend',()=>{touch=null},{passive:true});listen(root,'touchcancel',()=>{touch=null},{passive:true});
    listen(packagePanel,'keydown',e=>{
      const index={ArrowLeft:selected-1,ArrowRight:selected+1,Home:0,End:3}[e.key];
      if(index===undefined)return;
      e.preventDefault();const target=Math.max(0,Math.min(3,index));selectSetup(target);packages[target].focus({preventScroll:true});
    });
    steps.forEach((n,i)=>listen(n,'click',()=>select(i)));
    listen(root.querySelector('.mxj-steps'),'keydown',e=>{const index={ArrowLeft:phase-1,ArrowRight:phase+1,Home:0,End:2}[e.key];if(index!==undefined){e.preventDefault();select(index);steps[Math.max(0,Math.min(2,index))].focus({preventScroll:true})}});
    cards.forEach(card=>listen(card,'pointerdown',e=>{if(root.dataset.inspection!=='true'||e.pointerType==='mouse')return;const old=card.classList.contains('is-inspecting');cards.forEach(n=>n.classList.remove('is-inspecting'));if(!old)card.classList.add('is-inspecting')}));
    listen(window,'scroll',read,{passive:true});listen(window,'resize',resize);listen(window.visualViewport,'resize',resize);listen(reduced,'change',resize);listen(document,'visibilitychange',keypress);
    const observer=new IntersectionObserver(es=>{visible=es[0].isIntersecting;keypress()});observer.observe(root);
    listen(document,'visibilitychange',backlight);
    const headerObserver=new ResizeObserver(resize),header=document.querySelector('#header');if(header)headerObserver.observe(header);
    const anchors={'#modules':0,'#craft':1,'#configuration':2,'#hardware-journey':0};
    const jump=()=>{if(location.hash in anchors)select(anchors[location.hash])};
    listen(window,'hashchange',jump);
    listen(document,'click',e=>{const a=e.target.closest('a[href]');if(!a||e.ctrlKey||e.metaKey||e.shiftKey||e.altKey||e.button!==0)return;const url=new URL(a.href,location.href);if(url.origin===location.origin&&url.pathname===location.pathname&&url.hash in anchors){e.preventDefault();history.replaceState(null,'',url.hash);select(anchors[url.hash])}});
    listen(root,'shopify:block:select',()=>select(0,.92));
    root.mxJourneySelect=select;root.mxJourneySeek=seek;root.classList.add('is-ready');resize();
    const loaded=document.readyState==='complete'?Promise.resolve():new Promise(resolve=>listen(window,'load',resolve,{once:true}));
    Promise.all([loaded,document.fonts.ready]).then(()=>{if(alive){resize();if(location.hash in anchors)requestAnimationFrame(jump)}});
    instances.set(root,()=>{alive=false;events.abort();observer.disconnect();headerObserver.disconnect();cancelAnimationFrame(raf);cancelAnimationFrame(craftFrame);clearTimeout(keyTimer);setupFade?.cancel();keys.forEach(n=>n.remove());delete root.mxJourneySelect;delete root.mxJourneySeek;root.classList.remove('is-ready','is-breathing');copies.forEach(n=>{n.inert=false;n.removeAttribute('aria-hidden')});packagePanel.inert=false;packagePanel.removeAttribute('aria-hidden')});
  }
  const scan=(scope=document)=>scope.querySelectorAll('[data-hardware-journey]').forEach(mount);
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',()=>scan(),{once:true});else scan();
  document.addEventListener('shopify:section:load',e=>scan(e.target));
  document.addEventListener('shopify:section:unload',e=>{for(const[root,dispose]of instances)if(e.target.contains(root)){dispose();instances.delete(root)}});
})();
